import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, Card, Form, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useData, useTaskChecklistItems, useTaskStats } from "../../storage/accessors";
import { useLicense } from "../../hooks/useLicense";
import { useNavigate, useLocation } from "react-router";
import { Task } from "../../state/Task";
import Icon from "../Icon";
import useWheelId from "../../hooks/useWheelId";
import MonthGrid from "./MonthGrid";
import { faCheck, faPlus as add, faTrash as trash, faRoad, faArrowUp, faStar, faSearch, faGripVertical, faThumbtack, faExternalLinkAlt, faClock, faCalendar } from "@fortawesome/free-solid-svg-icons";
import { format, parse } from "date-fns";
import { ru } from "date-fns/locale";
import { deadlineColor } from "../../state/helpers";

const PRIMARY_COLOR = "var(--bs-primary)";
const PRIMARY_BG = "rgba(13, 110, 253, 0.08)";

type Props = {
    lifepartUuid: string;
    currentStartpoint: number;
    onUpgrade: (step: number) => void;
    onOpenTask?: (taskUuid: string) => void;
    isFocused?: boolean;
    onToggleFocus?: () => void;
    showNavigateButton?: boolean;
};

function TaskItem({
    task,
    onToggleDone,
    onDeleteTask,
    onOpenTask,
    draggable,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
}: {
    task: Task;
    onToggleDone: () => void;
    onDeleteTask: () => void;
    onOpenTask?: (taskUuid: string) => void;
    draggable?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent) => void;
    onDragEnd?: (e: React.DragEvent) => void;
    onTouchStart?: (e: React.TouchEvent) => void;
    onTouchMove?: (e: React.TouchEvent) => void;
    onTouchEnd?: (e: React.TouchEvent) => void;
}) {
    const { tasks: allTasks } = useData();
    const checklistItems = useTaskChecklistItems(task.uuid);
    const stats = useTaskStats(task, checklistItems);
    const cardRef = useRef<HTMLDivElement>(null);

    // Проверяем, есть ли у задачи или её подзадач запланированные месяцы
    const hasPlanning = (task.month && task.month.length > 0) || 
                        checklistItems.some(item => {
                            if (!item.innerTask) return false;
                            const innerTaskObj = allTasks.objects[item.innerTask];
                            return innerTaskObj && innerTaskObj.month && innerTaskObj.month.length > 0;
                        });

    const handleMouseDown = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const handle = target.closest('[data-drag-handle="true"]');
        if (handle && cardRef.current) {
            cardRef.current.dataset.dragAllowed = 'true';
        }
    };

    const handleDragStart = (e: React.DragEvent) => {
        const card = cardRef.current;
        if (!card || card.dataset.dragAllowed !== 'true') {
            e.preventDefault();
            if (card) delete card.dataset.dragAllowed;
            return;
        }
        delete card.dataset.dragAllowed;
        onDragStart?.(e);
    };

    return (
        <div
            ref={cardRef}
            className="d-flex flex-column gap-1 p-2 border rounded bg-body"
            style={task.isDone ? { opacity: 0.6 } : undefined}
            data-task-uuid={task.uuid}
            draggable={draggable}
            onMouseDown={handleMouseDown}
            onDragStart={handleDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
        >
            <div className="d-flex align-items-center gap-2">
                {draggable && (
                    <span
                        className="text-muted"
                        style={{ cursor: "grab", touchAction: "none" }}
                        data-drag-handle="true"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <Icon icon={faGripVertical} style={{ fontSize: "0.75rem" }} />
                    </span>
                )}
                <Form.Check
                    type="checkbox"
                    checked={task.isDone}
                    onChange={onToggleDone}
                />
                <span
                    style={{
                        flex: 1,
                        textDecoration: task.isDone ? "line-through" : "none",
                        color: task.isDone ? "gray" : "inherit",
                        cursor: onOpenTask ? "pointer" : "default",
                        fontSize: "0.9rem",
                    }}
                    onClick={() => onOpenTask?.(task.uuid)}
                >
                    {task.description}
                    {!task.isDone && !hasPlanning && (
                        <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: "0.65rem" }}>
                            Не запланирована
                        </Badge>
                    )}
                    {!task.isDone && task.deadline && (
                        <Badge bg={deadlineColor(task.deadline)} className="ms-2" style={{ fontSize: "0.65rem" }}>
                            <Icon icon={faClock} size="sm" className="me-1" style={{ fontSize: "0.6rem" }} />
                            {format(parse(task.deadline, "yyyy-MM-dd", new Date()), "dd.MM", { locale: ru })}
                        </Badge>
                    )}
                </span>
                <Button
                    variant="outline-danger"
                    size="sm"
                    className="py-0 px-1"
                    onClick={onDeleteTask}
                >
                    <Icon icon={trash} style={{ fontSize: "0.75rem" }} />
                </Button>
            </div>
            {stats.tasks > 0 && (
                <div style={{ cursor: onOpenTask ? "pointer" : "default" }} onClick={() => onOpenTask?.(task.uuid)}>
                    <div className="w-100 rounded" style={{ height: 4, backgroundColor: "#e9ecef" }}>
                        <div
                            className="rounded"
                            style={{
                                height: 4,
                                width: `${stats.finishedTasks / stats.tasks * 100}%`,
                                backgroundColor: "var(--bs-primary)",
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default function RoadMapWidget({ lifepartUuid, currentStartpoint, onUpgrade, onOpenTask, isFocused, onToggleFocus, showNavigateButton }: Props) {
    const { tasks, roles } = useData();
    const { getLimit } = useLicense();
    const navigate = useNavigate();
    const wheelId = useWheelId();
    const lifepart = roles.objects[lifepartUuid];

    const [showAddForm, setShowAddForm] = useState<number | null>(null);
    const [newTaskDescription, setNewTaskDescription] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showExistingTasks, setShowExistingTasks] = useState<number | null>(null);
    const [draggedTaskUuid, setDraggedTaskUuid] = useState<string | null>(null);
    const [dragOverStep, setDragOverStep] = useState<number | null>(null);
    const touchDraggedTaskUuid = useRef<string | null>(null);
    const touchCurrentTargetUuid = useRef<string | null>(null);
    const touchDragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    // Mobile touch drag preview state
    const [touchDragTask, setTouchDragTask] = useState<Task | null>(null);
    const [touchDragPos, setTouchDragPos] = useState<{ x: number; y: number } | null>(null);
    // const touchDragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    // Все шаги от 1 до 10
    const allSteps = useMemo(() => {
        return Array.from({ length: 10 }, (_, i) => i + 1);
    }, []);

    // Шаги, которые ещё нужно пройти (от currentStartpoint + 1 до 10)
    const remainingSteps = useMemo(() => {
        return Array.from({ length: 10 - currentStartpoint }, (_, i) => currentStartpoint + i + 1);
    }, [currentStartpoint]);

    const getStepTasks = (stepNumber: number) => {
        return Object.values(tasks.objects).filter(
            (task) => task.lifepart === lifepartUuid && task.forPoint === stepNumber && !task.parentTask
        );
    };

    const isStepReady = (stepNumber: number) => {
        if (stepNumber <= currentStartpoint) return true; // Уже пройден
        if (stepNumber !== currentStartpoint + 1) return false; // Не текущий
        const stepTasks = getStepTasks(stepNumber);
        return stepTasks.length > 0 && stepTasks.every((t) => t.isDone);
    };

    const checkTasksLimit = (): boolean => {
        const tasksCount = Object.values(tasks.objects).length;
        const tasksLimit = getLimit('limit_tasks');
        if (tasksCount >= tasksLimit) {
            navigate('/my/tariffs?reason=limit_tasks');
            return false;
        }
        return true;
    };

    const handleCreateTask = (stepNumber: number) => {
        if (!newTaskDescription.trim()) return;
        if (!checkTasksLimit()) return;

        const stepTasks = getSortedStepTasks(stepNumber);
        const newTask: Task = {
            uuid: crypto.randomUUID(),
            description: newTaskDescription.trim(),
            context: "",
            lifepart: lifepartUuid,
            forPoint: stepNumber,
            isDone: false,
            order: stepTasks.length,
            labels: [],
            stepOrder: stepTasks.length,
        };

        tasks.create(newTask);
        setNewTaskDescription("");
        setShowAddForm(null);
    };

    const handleAssignTask = (stepNumber: number, taskUuid: string) => {
        const stepTasks = getSortedStepTasks(stepNumber);
        tasks.save(taskUuid, { forPoint: stepNumber, stepOrder: stepTasks.length });
        setShowExistingTasks(null);
        setSearchTerm("");
    };

    const handleDeleteTask = (taskUuid: string) => {
        tasks.save(taskUuid, { forPoint: null });
    };

    const handleTaskMonthUpdate = (taskUuid: string, month: string[]) => {
        tasks.save(taskUuid, { month });
    };

    const handleToggleDone = (task: Task) => {
        tasks.save(task.uuid, { isDone: !task.isDone });
    };

    const handleDragStart = (e: React.DragEvent, taskUuid: string) => {
        setDraggedTaskUuid(taskUuid);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", taskUuid);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDragOverStep = (e: React.DragEvent, stepNumber: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOverStep(stepNumber);
    };

    const handleDragLeaveStep = () => {
        setDragOverStep(null);
    };

    const handleDropOnTask = useCallback((e: React.DragEvent, targetTaskUuid: string) => {
        e.preventDefault();
        if (!draggedTaskUuid || draggedTaskUuid === targetTaskUuid) {
            setDraggedTaskUuid(null);
            return;
        }

        const draggedTask = tasks.objects[draggedTaskUuid];
        const targetTask = tasks.objects[targetTaskUuid];

        if (!draggedTask || !targetTask) {
            setDraggedTaskUuid(null);
            return;
        }

        // Если задачи из разных шагов — перемещаем в новый шаг
        if (draggedTask.forPoint !== targetTask.forPoint) {
            // Нельзя перетаскивать в пройденные шаги
            if (targetTask.forPoint != null && targetTask.forPoint <= currentStartpoint) {
                setDraggedTaskUuid(null);
                return;
            }
            const newStep = targetTask.forPoint;
            const stepTasks = getStepTasks(newStep!).sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));
            tasks.save(draggedTaskUuid, { forPoint: newStep, stepOrder: stepTasks.length });
            setDraggedTaskUuid(null);
            return;
        }

        // Задачи в одном шаге — меняем порядок
        const step = draggedTask.forPoint!;
        const stepTasks = getStepTasks(step).sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));

        const draggedIndex = stepTasks.findIndex(t => t.uuid === draggedTaskUuid);
        const targetIndex = stepTasks.findIndex(t => t.uuid === targetTaskUuid);

        if (draggedIndex === -1 || targetIndex === -1) {
            setDraggedTaskUuid(null);
            return;
        }

        const reordered = [...stepTasks];
        const [moved] = reordered.splice(draggedIndex, 1);
        reordered.splice(targetIndex, 0, moved);

        for (let i = 0; i < reordered.length; i++) {
            tasks.save(reordered[i].uuid, { stepOrder: i });
        }

        setDraggedTaskUuid(null);
    }, [draggedTaskUuid, tasks, currentStartpoint]);

    const handleDropOnStep = useCallback((e: React.DragEvent, stepNumber: number) => {
        e.preventDefault();
        setDragOverStep(null);
        if (!draggedTaskUuid) {
            setDraggedTaskUuid(null);
            return;
        }

        const draggedTask = tasks.objects[draggedTaskUuid];
        if (!draggedTask) {
            setDraggedTaskUuid(null);
            return;
        }

        // Нельзя перетаскивать в пройденные шаги
        if (stepNumber <= currentStartpoint) {
            setDraggedTaskUuid(null);
            return;
        }

        // Если задача уже в этом шаге — ничего не делаем
        if (draggedTask.forPoint === stepNumber) {
            setDraggedTaskUuid(null);
            return;
        }

        const stepTasks = getStepTasks(stepNumber).sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));
        tasks.save(draggedTaskUuid, { forPoint: stepNumber, stepOrder: stepTasks.length });
        setDraggedTaskUuid(null);
    }, [draggedTaskUuid, tasks, currentStartpoint]);

    const handleDragEnd = () => {
        setDraggedTaskUuid(null);
    };

    const handleTouchStart = (e: React.TouchEvent, taskUuid: string) => {
        touchDraggedTaskUuid.current = taskUuid;
        touchCurrentTargetUuid.current = null;
        const task = tasks.objects[taskUuid];
        if (task) {
            setTouchDragTask(task);
            const touch = e.touches[0];
            // Найти DOM-элемент карточки для вычисления offset
            const target = (e.target as HTMLElement).closest('[data-task-uuid]') as HTMLElement | null;
            if (target) {
                const rect = target.getBoundingClientRect();
                touchDragOffset.current = {
                    x: touch.clientX - rect.left,
                    y: touch.clientY - rect.top,
                };
            } else {
                touchDragOffset.current = { x: 0, y: 0 };
            }
            setTouchDragPos({ x: touch.clientX, y: touch.clientY });
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!touchDraggedTaskUuid.current) return;

        const touch = e.touches[0];
        setTouchDragPos({ x: touch.clientX, y: touch.clientY });

        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);

        if (elementBelow) {
            const targetCard = elementBelow.closest('[data-task-uuid]');
            const targetUuid = targetCard?.getAttribute('data-task-uuid');

            if (targetUuid && targetUuid !== touchDraggedTaskUuid.current) {
                touchCurrentTargetUuid.current = targetUuid;
            }
        }
    };

    const handleTouchEnd = useCallback((e: React.TouchEvent, targetStepNumber?: number) => {
        if (!touchDraggedTaskUuid.current) {
            touchDraggedTaskUuid.current = null;
            touchCurrentTargetUuid.current = null;
            return;
        }

        const fromUuid = touchDraggedTaskUuid.current;
        const draggedTask = tasks.objects[fromUuid];

        if (!draggedTask) {
            touchDraggedTaskUuid.current = null;
            touchCurrentTargetUuid.current = null;
            return;
        }

        // Определяем элемент, на котором завершено касание
        const touch = e.changedTouches[0];
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);

        // Проверяем, дропнули ли на шаг (заголовок шага)
        const droppedOnStepHeader = elementBelow?.closest('[data-step-header="true"]');
        const droppedStepNumber = droppedOnStepHeader?.getAttribute('data-step-number');

        // Проверяем, дропнули ли на задачу
        const droppedOnTask = elementBelow?.closest('[data-task-uuid]');
        const droppedTaskUuid = droppedOnTask?.getAttribute('data-task-uuid');

        // Приоритет: если дроп на задачу — обрабатываем как дроп на задачу
        // Если дроп на заголовок шага — обрабатываем как дроп на шаг
        // Иначе используем targetStepNumber из пропса

        let targetStep: number | undefined;
        let targetTask: string | undefined;

        if (droppedTaskUuid && droppedTaskUuid !== fromUuid) {
            // Дроп на задачу
            targetTask = droppedTaskUuid;
            const task = tasks.objects[droppedTaskUuid];
            if (task) {
                targetStep = task.forPoint ?? undefined;
            }
        } else if (droppedStepNumber) {
            // Дроп на заголовок шага
            targetStep = parseInt(droppedStepNumber, 10);
        } else if (targetStepNumber !== undefined) {
            // Фолбэк: используем переданный шаг
            targetStep = targetStepNumber;
        }

        // Если ничего не определено — отменяем
        if (targetStep === undefined && targetTask === undefined) {
            touchDraggedTaskUuid.current = null;
            touchCurrentTargetUuid.current = null;
            setTouchDragTask(null);
            setTouchDragPos(null);
            return;
        }

        // Нельзя перетаскивать в пройденные шаги
        if (targetStep !== undefined && targetStep <= currentStartpoint) {
            touchDraggedTaskUuid.current = null;
            touchCurrentTargetUuid.current = null;
            setTouchDragTask(null);
            setTouchDragPos(null);
            return;
        }

        // Если дроп на задачу
        if (targetTask) {
            const targetTaskObj = tasks.objects[targetTask];
            if (!targetTaskObj) {
                touchDraggedTaskUuid.current = null;
                touchCurrentTargetUuid.current = null;
                setTouchDragTask(null);
                setTouchDragPos(null);
                return;
            }

            // Если задачи из разных шагов — перемещаем в новый шаг
            if (draggedTask.forPoint !== targetTaskObj.forPoint) {
                // Нельзя перетаскивать в пройденные шаги
                if (targetTaskObj.forPoint != null && targetTaskObj.forPoint <= currentStartpoint) {
                    touchDraggedTaskUuid.current = null;
                    touchCurrentTargetUuid.current = null;
                    setTouchDragTask(null);
                    setTouchDragPos(null);
                    return;
                }
                const newStep = targetTaskObj.forPoint;
                const stepTasks = getStepTasks(newStep!).sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));
                tasks.save(fromUuid, { forPoint: newStep, stepOrder: stepTasks.length });
                touchDraggedTaskUuid.current = null;
                touchCurrentTargetUuid.current = null;
                setTouchDragTask(null);
                setTouchDragPos(null);
                return;
            }

            // Задачи в одном шаге — меняем порядок
            const step = draggedTask.forPoint!;
            const stepTasks = getStepTasks(step).sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));

            const draggedIndex = stepTasks.findIndex(t => t.uuid === fromUuid);
            const targetIndex = stepTasks.findIndex(t => t.uuid === targetTask);

            if (draggedIndex === -1 || targetIndex === -1) {
                touchDraggedTaskUuid.current = null;
                touchCurrentTargetUuid.current = null;
                setTouchDragTask(null);
                setTouchDragPos(null);
                return;
            }

            const reordered = [...stepTasks];
            const [moved] = reordered.splice(draggedIndex, 1);
            reordered.splice(targetIndex, 0, moved);

            for (let i = 0; i < reordered.length; i++) {
                tasks.save(reordered[i].uuid, { stepOrder: i });
            }

            touchDraggedTaskUuid.current = null;
            touchCurrentTargetUuid.current = null;
            setTouchDragTask(null);
            setTouchDragPos(null);
            return;
        }

        // Дроп на шаг (без задачи)
        if (targetStep !== undefined) {
            // Если задача уже в этом шаге — ничего не делаем
            if (draggedTask.forPoint === targetStep) {
                touchDraggedTaskUuid.current = null;
                touchCurrentTargetUuid.current = null;
                setTouchDragTask(null);
                setTouchDragPos(null);
                return;
            }
            const stepTasks = getStepTasks(targetStep).sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));
            tasks.save(fromUuid, { forPoint: targetStep, stepOrder: stepTasks.length });
            touchDraggedTaskUuid.current = null;
            touchCurrentTargetUuid.current = null;
            setTouchDragTask(null);
            setTouchDragPos(null);
            return;
        }

        touchDraggedTaskUuid.current = null;
        touchCurrentTargetUuid.current = null;
        setTouchDragTask(null);
        setTouchDragPos(null);
    }, [tasks, currentStartpoint, getStepTasks]);

    const isPassed = (step: number) => step <= currentStartpoint;
    const isCurrent = (step: number) => step === currentStartpoint + 1;
    const isCurrentReady = (step: number) => isCurrent(step) && isStepReady(step);

    const getSortedStepTasks = (stepNumber: number) => {
        return getStepTasks(stepNumber).sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));
    };

    const totalRemaining = remainingSteps.length;
    const completedRemaining = remainingSteps.filter((s) => isStepReady(s)).length;

    return (
        <Card className="border-2 shadow-sm" style={{ borderColor: PRIMARY_COLOR }}>
            <Card.Header
                className="d-flex flex-row flex-wrap justify-content-between align-items-center gap-2"
                style={{ backgroundColor: PRIMARY_BG }}
            >
                <div className="d-flex align-items-center gap-2">
                    <Icon icon={faRoad} style={{ color: PRIMARY_COLOR }} />
                    <strong style={{ color: PRIMARY_COLOR }}>Путь</strong>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/${wheelId}/plan?lifepart=${lifepartUuid}`)}
                    >
                        <Icon icon={faCalendar} className="me-1" />
                        Перейти в План
                    </Button>
                    {showNavigateButton && (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id="roadmap-lifepart-tooltip">
                                    Перейти к этой части жизни
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="p-0"
                                style={{ width: 32, height: 32, minWidth: 32 }}
                                onClick={() => navigate(`/${wheelId}/roles/${lifepartUuid}`)}
                            >
                                <Icon icon={faExternalLinkAlt} style={{ fontSize: "0.85rem" }} />
                            </Button>
                        </OverlayTrigger>
                    )}
                    {onToggleFocus && (
                        <OverlayTrigger
                            placement="left"
                            overlay={
                                <Tooltip id="roadmap-focus-tooltip">
                                    {isFocused
                                        ? 'Этот путь отображается на главной странице «Я». Нажмите, чтобы открепить.'
                                        : 'Закрепить этот путь на главной странице «Я» рядом с блоком «Моя миссия».'}
                                </Tooltip>
                            }
                        >
                            <Button
                                variant={isFocused ? "success" : "outline-primary"}
                                size="sm"
                                onClick={onToggleFocus}
                            >
                                <Icon icon={faThumbtack} className="me-1" />
                                {isFocused ? "Закреплено" : "Закрепить на главной"}
                            </Button>
                        </OverlayTrigger>
                    )}
                </div>
            </Card.Header>

            <Card.Body>

                {/* Вертикальный список всех шагов */}
                <div className="position-relative" style={{ paddingLeft: 24 }}>
                    {/* Вертикальная линия пути */}
                    <div
                        className="position-absolute"
                        style={{
                            left: 11,
                            top: 12,
                            bottom: 48,
                            width: 2,
                            backgroundColor: PRIMARY_COLOR,
                            opacity: 0.2,
                        }}
                    />

                    {(() => {
                        const passedSteps = allSteps.filter(isPassed);
                        const nonPassedSteps = allSteps.filter((s) => !isPassed(s));

                        const renderPassedStep = (step: number) => (
                            <div key={step} className="mb-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="position-relative flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                            width: 24,
                                            height: 24,
                                            backgroundColor: "#198754",
                                            color: "#fff",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold",
                                            zIndex: 1,
                                            border: "2px solid #198754",
                                        }}
                                    >
                                        {step}
                                    </div>
                                    <div style={{ fontSize: "0.9rem", color: "#198754" }}>
                                        <Icon icon={faCheck} style={{ fontSize: "0.75rem", marginRight: 4 }} />
                                        Шаг {step}
                                    </div>
                                </div>
                            </div>
                        );

                        return (
                            <>
                                {passedSteps.length > 2 ? (
                                    <>
                                        {renderPassedStep(passedSteps[0])}
                                        <div className="mb-3 d-flex align-items-center gap-3">
                                            <div
                                                className="position-relative flex-shrink-0 d-flex flex-column align-items-center justify-content-center gap-1"
                                                style={{ width: 24, height: 24, zIndex: 1 }}
                                            >
                                                <div style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "#198754" }} />
                                                <div style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "#198754" }} />
                                                <div style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "#198754" }} />
                                            </div>
                                        </div>
                                        {renderPassedStep(passedSteps[passedSteps.length - 1])}
                                    </>
                                ) : (
                                    passedSteps.map(renderPassedStep)
                                )}

                                {nonPassedSteps.map((step) => {
                                    const stepTasks = getSortedStepTasks(step);
                                    const current = isCurrent(step);
                                    const currentReady = isCurrentReady(step);
                                    const stepName = lifepart?.stepNames?.[String(step)] || "";

                                    return (
                                        <div key={step} className="mb-3">
                                            {/* Точка шага */}
                                            <div
                                                data-step-header="true"
                                                data-step-number={step}
                                                className="d-flex flex-wrap align-items-center gap-3"
                                                style={{
                                                    cursor: "pointer",
                                                    backgroundColor: dragOverStep === step ? "rgba(13, 110, 253, 0.08)" : "transparent",
                                                    borderRadius: "4px",
                                                    padding: "4px",
                                                    transition: "background-color 0.2s ease",
                                                }}
                                                onDragOver={(e) => handleDragOverStep(e, step)}
                                                onDragLeave={handleDragLeaveStep}
                                                onDrop={(e) => handleDropOnStep(e, step)}
                                            >
                                                {/* Круг с номером шага */}
                                                <div
                                                    className="position-relative flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle"
                                                    style={{
                                                        width: currentReady ? 28 : 24,
                                                        height: currentReady ? 28 : 24,
                                                        backgroundColor: currentReady ? "#20c997" : PRIMARY_COLOR,
                                                        color: "#fff",
                                                        fontSize: currentReady ? "0.85rem" : "0.75rem",
                                                        fontWeight: "bold",
                                                        border: `2px solid ${currentReady ? "#20c997" : PRIMARY_COLOR}`,
                                                        zIndex: 1,
                                                        transition: "all 0.2s ease",
                                                        boxShadow: current && !currentReady ? `0 0 0 4px rgba(13, 110, 253, 0.15)` : undefined,
                                                    }}
                                                >
                                                    {currentReady ? <Icon icon={faCheck} style={{ fontSize: "0.7rem" }} /> : step}
                                                </div>

                                                {/* Информация о шаге */}
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <strong style={{ fontSize: "0.95rem" }}>
                                                            {currentReady
                                                                ? (stepName ? `${stepName} — готово к повышению!` : `Шаг ${step} — готово к повышению!`)
                                                                : (stepName || `Шаг ${step}`)
                                                            }
                                                        </strong>
                                                        {current && !currentReady && (
                                                            <Badge bg="primary" style={{ fontSize: "0.65rem" }}>
                                                                Текущий шаг
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                                                        {stepTasks.length === 0
                                                            ? "Нет задач"
                                                            : `${stepTasks.filter((t) => t.isDone).length} из ${stepTasks.length} выполнено`
                                                        }
                                                    </div>
                                                </div>

                                                {/* Кнопка повышения */}
                                                {currentReady && (
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        className="flex-shrink-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onUpgrade(step);
                                                        }}
                                                    >
                                                        <Icon icon={faArrowUp} className="me-1" />
                                                        Повысить
                                                    </Button>
                                                )}
                                            </div>

                                            {/* Задачи шага */}
                                            <div className="mt-2 ms-md-4">
                                                {stepTasks.length === 0 ? (
                                                    <div className="text-muted py-2" style={{ fontSize: "0.85rem" }}>
                                                        <strong>Результаты:</strong> Опишите 1-3 результата для данного шага движения к цели
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                                                            <strong>Результаты:</strong>
                                                        </div>
                                                        <div
                                                            className="d-flex flex-column gap-2 mb-2"
                                                            onTouchMove={handleTouchMove}
                                                            onTouchEnd={(e) => handleTouchEnd(e, step)}
                                                        >
                                                            {stepTasks.map((task) => (
                                                                <TaskItem
                                                                    key={task.uuid}
                                                                    task={task}
                                                                    onToggleDone={() => handleToggleDone(task)}
                                                                    onDeleteTask={() => handleDeleteTask(task.uuid)}
                                                                    onOpenTask={onOpenTask}
                                                                    draggable
                                                                    onDragStart={(e) => handleDragStart(e, task.uuid)}
                                                                    onDragOver={handleDragOver}
                                                                    onDrop={(e) => handleDropOnTask(e, task.uuid)}
                                                                    onDragEnd={handleDragEnd}
                                                                    onTouchStart={(e) => handleTouchStart(e, task.uuid)}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Форма создания новой задачи */}
                                                {showAddForm === step && (
                                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            placeholder="опишите результат"
                                                            value={newTaskDescription}
                                                            onChange={(e: any) => setNewTaskDescription(e.target.value)}
                                                            onKeyDown={(e: any) => e.key === "Enter" && handleCreateTask(step)}
                                                            autoFocus
                                                            style={{ minWidth: 0, flex: "1 1 120px" }}
                                                        />
                                                        <div className="d-flex gap-2">
                                                            <Button variant="primary" size="sm" onClick={() => handleCreateTask(step)}>
                                                                <Icon icon={add} />
                                                            </Button>
                                                            <Button variant="outline-secondary" size="sm" onClick={() => { setShowAddForm(null); setNewTaskDescription(""); }}>
                                                                Отмена
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Форма выбора существующей задачи */}
                                                {showExistingTasks === step && (
                                                    <div className="mt-2">
                                                        <Form.Control
                                                            type="text"
                                                            size="sm"
                                                            placeholder="Поиск задачи..."
                                                            value={searchTerm}
                                                            onChange={(e: any) => setSearchTerm(e.target.value)}
                                                            autoFocus
                                                            className="mb-2"
                                                        />
                                                        <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                                                            {(() => {
                                                                const available = Object.values(tasks.objects).filter(
                                                                    (t) => t.lifepart === lifepartUuid && !t.forPoint && !t.isDone
                                                                );
                                                                const filtered = searchTerm
                                                                    ? available.filter((t) => t.description.toLowerCase().includes(searchTerm.toLowerCase()))
                                                                    : available;
                                                                return filtered.length === 0 ? (
                                                                    <div className="text-muted py-1" style={{ fontSize: "0.8rem" }}>
                                                                        Нет доступных задач
                                                                    </div>
                                                                ) : (
                                                                    filtered.map((t) => (
                                                                        <div
                                                                            key={t.uuid}
                                                                            className="d-flex align-items-center gap-2 p-1 border-bottom"
                                                                            style={{ cursor: "pointer", fontSize: "0.85rem" }}
                                                                            onClick={() => handleAssignTask(step, t.uuid)}
                                                                        >
                                                                            <Icon icon={add} style={{ fontSize: "0.7rem" }} />
                                                                            <span className="text-truncate">{t.description}</span>
                                                                        </div>
                                                                    ))
                                                                );
                                                            })()}
                                                        </div>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="mt-2"
                                                            onClick={() => { setShowExistingTasks(null); setSearchTerm(""); }}
                                                        >
                                                            Отмена
                                                        </Button>
                                                    </div>
                                                )}

                                                {/* Кнопки добавления */}
                                                {showAddForm !== step && showExistingTasks !== step && (
                                                    <div className="d-flex flex-column flex-sm-row gap-2 mt-2">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => setShowAddForm(step)}
                                                        >
                                                            <Icon icon={add} className="me-1" /> Новый желаемый результат
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => setShowExistingTasks(step)}
                                                        >
                                                            <Icon icon={faSearch} className="me-1" /> Из списка
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        );
                    })()}

                    {/* Финальная точка — 10 */}
                    <div
                        data-roadmap-drop="true"
                        className="d-flex align-items-center gap-3"
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            const taskUuid = e.dataTransfer.getData("text/plain");
                            if (taskUuid && tasks.objects[taskUuid]) {
                                tasks.save(taskUuid, { month: [] });
                            }
                        }}
                        style={{ cursor: "default" }}
                    >
                        <div
                            className="position-relative flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle"
                            style={{
                                width: 24,
                                height: 24,
                                backgroundColor: currentStartpoint === 10 ? "#ffc107" : "#fff",
                                color: currentStartpoint === 10 ? "#000" : "#6c757d",
                                fontSize: "0.75rem",
                                fontWeight: "bold",
                                border: `2px solid ${currentStartpoint === 10 ? "#ffc107" : "#dee2e6"}`,
                                zIndex: 1,
                            }}
                        >
                            <Icon icon={faStar} style={{ fontSize: "0.7rem" }} />
                        </div>
                        <div>
                            <strong style={{ fontSize: "0.95rem" }}>
                                {currentStartpoint === 10 ? "Цель достигнута!" : "Цель"}
                            </strong>
                            <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                Перетащите сюда, чтобы удалить месяц
                            </div>
                            {currentStartpoint === 10 && (
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                                    Поздравляем! Эта сфера баланса на максимуме
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card.Body>

            {/* Сетка месяцев */}
            {/* <MonthGrid
                lifepartUuid={lifepartUuid}
                tasks={tasks.objects}
                roles={roles}
                onTaskMonthUpdate={handleTaskMonthUpdate}
                onOpenTask={onOpenTask}
            /> */}

            {/* Mobile touch drag preview overlay */}
            {touchDragTask && touchDragPos && (
                <div
                    style={{
                        position: 'fixed',
                        left: touchDragPos.x - touchDragOffset.current.x,
                        top: touchDragPos.y - touchDragOffset.current.y,
                        width: 'auto',
                        minWidth: 200,
                        maxWidth: '90vw',
                        zIndex: 9999,
                        pointerEvents: 'none',
                        opacity: 0.9,
                        transform: 'rotate(2deg)',
                    }}
                >
                    <div className="d-flex flex-column gap-1 p-2 border rounded bg-body shadow">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted" style={{ cursor: 'grab' }}>
                                <Icon icon={faGripVertical} style={{ fontSize: '0.75rem' }} />
                            </span>
                            <Form.Check type="checkbox" checked={touchDragTask.isDone} readOnly />
                            <span
                                style={{
                                    flex: 1,
                                    textDecoration: touchDragTask.isDone ? 'line-through' : 'none',
                                    color: touchDragTask.isDone ? 'gray' : 'inherit',
                                    fontSize: '0.9rem',
                                }}
                            >
                                {touchDragTask.description}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
}